import { FormikActions, FormikErrors } from 'formik';
import React from 'react';
import { FormattedMessage, InjectedIntl, injectIntl } from 'react-intl';
import styled, { keyframes } from 'styled-components';
import { COLORS, FONT_FAMILY } from '../../../app/common/constants/styles';
import { DEFAULT_MESSAGE } from '../../../i18n/defaultMessage.constant';
import { getPayVaultStylesByScreenSize } from './PayVaultStyles';
import {
    IPayVaultAPISuccessResponse,
    IPayVaultElements,
} from '../../../api/payVault/payVault.interface';
import { PayVaultAPIService } from '../../../api/payVault/payVaultAPI';
import { PayVaultAPIFormKeys } from '../../../api/payVault/payVaultAPIForm.constants';
import Checkbox from '../../../app/common/components/Checkbox';
import Form from '../../../app/common/components/Form';
import AddressSelector from '../../../app/common/fields/AddressSelector';
import SwitchField from '../../../app/common/fields/SwitchField';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import {
    IAddress,
    IAddressRequest,
    IAddressFieldConfig,
} from '../../addresses/address/address.interface';
import { ICard, ICardPostRequest, ICardPutRequest } from '../cards/card/card.interface';
import { CardFields, ICardFormValues } from '../cards/cardForm/CardFields';
import { SupportedClientCountryCode } from '../../../config/constants/supportedClientCountryCode.enum';
import { FormManditoryAstrixMessage } from '../../../app/common/components/FormManitoryMessage';
import { SupportedLanguage } from '../../../config/constants/supportedLanguage.enum';
import { getChallengeData } from '../../../utils/challengerMain';
import { RegionHelper } from '../../../utils/RegionHelper';
import { getFullYear } from '../../../app/common/validator/DobValidator';
import { PaymentMethodRatEventTracker } from '../paymentMethodRatEvents';

export interface IPaymentMethodFormValues extends ICardFormValues {
    cardNumber: string;
    brandName: string;
    addressUuid?: string;
    isPrimary: boolean;
    expireMonth: string;
    expireYear: string;
    ownerName: string;
    firstName?: string;
    lastName?: string;
    postalCode?: string;
    companyName?: string;
    countryCode?: string;
    state?: string;
    stateCode?: string;
    city?: string;
    addressLine1?: string;
    addressLine2?: string;
    phoneNumber?: string;
    isShipping?: boolean;
    challengerPid?: string;
}

export interface IPaymentMethodFormOwnProps {
    card?: ICard;
    isOnlyCard: boolean;
    onSubmit: () => any;
    showBillingAddress: boolean;
    brandCodes: string[];
}
export interface IPaymentMethodInjectedProps {
    intl: InjectedIntl;
}
export interface IPaymentMethodFormStateProps {
    addresses?: IAddress[];
    billingAddress?: IAddress;
    loading: boolean;
    defaultCountry: SupportedClientCountryCode;
    region?: string;
    paymethod: any;
    locale: SupportedLanguage;
    challengerPid: string;
    challengerDegenerate?: boolean;
    addressFieldConfig: IAddressFieldConfig[][];
    addressTemplate?: string;
    serviceName: string;
    privacyPolicyUrl: string;
    cardRestrictionEnabled: boolean;
}

export interface IPaymentMethodFormDispatchProps {
    addCard: (Card: ICardPostRequest) => void;
    addCardWithNewAddress: (
        card: ICardPutRequest | ICardPostRequest,
        address: IAddressRequest,
        updateCardToken?: string,
    ) => void;
    updateCard: (cardToken: string, card: ICardPutRequest) => void;
}

export type PaymentMethodFormProps = IPaymentMethodFormOwnProps &
    IPaymentMethodFormStateProps &
    IPaymentMethodFormDispatchProps &
    IPaymentMethodInjectedProps;

interface IPaymentMethodFormState {
    initialValues: IPaymentMethodFormValues;
    isEditing: boolean;
    isPrimaryCard: boolean;
    isSubmitting: boolean;
    isPayVaultLoaded: boolean;
    consentGiven: boolean;
}

const Title = styled.h2`
    font-size: 1.25rem;
    font-weight: normal;
    margin-top: 45px;
`;

const PrivacyPolicyLink = styled.a`
    color: ${COLORS.lightBlue};
    text-decoration: underline;
`;

const ConsentCheckboxWrapper = styled.div`
    margin-top: 24px;

    & label {
        height: auto;
        align-items: flex-start;
        padding-left: 32px;
        font-size: 1rem;
        font-weight: 400;
        line-height: 1.5rem;
        letter-spacing: 0;
        color: ${COLORS.dark2};
    }

    & label > span:nth-child(2) {
        top: 50%;
        transform: translateY(-50%);
    }

    & label > span:nth-child(2) > span {
        margin-top: -14px;
    }

    & label > span:nth-child(3) {
        padding-top: 0;
    }
`;

class PaymentMethodForm extends React.Component<PaymentMethodFormProps, IPaymentMethodFormState> {
    private ratTracker = new PaymentMethodRatEventTracker();

    constructor(props: PaymentMethodFormProps) {
        super(props);

        const { card, loading, isOnlyCard, defaultCountry } = this.props;

        const { billingAddress } = this.props;

        const cardInitialValues = card
            ? {
                  ...card,
                  expireYear: card.expireYear.slice(2, 4),
                  // Clear stale addressUuid if the linked address no longer exists
                  addressUuid: billingAddress ? card.addressUuid : '',
              }
            : {
                  addressUuid: '',
                  isPrimary: isOnlyCard,
                  brandName: '',
                  cardNumber: '',
                  expireMonth: '',
                  expireYear: '',
                  ownerName: '',
              };

        const initialValues: IPaymentMethodFormValues = {
            ...cardInitialValues,
            cardNumber: '',
            firstName: '',
            lastName: '',
            companyName: '',
            countryCode: defaultCountry,
            addressLine1: '',
            addressLine2: '',
            city: '',
            postalCode: '',
            phoneNumber: '',
            state: '',
            stateCode: '',
            isShipping: false,
        };

        this.state = {
            initialValues,
            isEditing: Boolean(card),
            isPrimaryCard: Boolean(card && card.isPrimary),
            isSubmitting: loading,
            isPayVaultLoaded: false,
            consentGiven: Boolean(card), // If editing an existing card, consent is already given
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleConsentToggle = this.handleConsentToggle.bind(this);
        this.renderExtraButtons = this.renderExtraButtons.bind(this);
        this.renderFormFields = this.renderFormFields.bind(this);
        this.handleSubmitAttemptWithErrors = this.handleSubmitAttemptWithErrors.bind(this);
    }

    public componentDidUpdate(prevProps: Readonly<PaymentMethodFormProps>): void {
        const { loading } = this.props;
        if (loading !== prevProps.loading) {
            this.setState({ isSubmitting: loading });
        }
    }

    public componentWillUnmount() {
        PayVaultAPIService.cleanPayVaultLoadedCheck();
    }

    private handleSubmitAttemptWithErrors(
        values: IPaymentMethodFormValues,
        errors: FormikErrors<IPaymentMethodFormValues>,
    ) {
        const { region } = this.props;
        const { isEditing } = this.state;

        this.ratTracker.trackValidationErrors(values, errors, isEditing);
    }

    private handleConsentToggle() {
        this.setState((prev) => ({ consentGiven: !prev.consentGiven }));
    }

    // Show consent checkbox only when adding a new card in EU/AP regions (not JP)
    private get showConsentCheckbox(): boolean {
        return (
            !this.state.isEditing &&
            (RegionHelper.isEU() || RegionHelper.isAP()) &&
            this.props.cardRestrictionEnabled
        );
    }

    private renderConsentLabelNode(policyLinkText: string): React.ReactNode {
        const { privacyPolicyUrl, serviceName } = this.props;
        const privacyPolicyLinkElement = privacyPolicyUrl ? (
            <PrivacyPolicyLink href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                {policyLinkText}
            </PrivacyPolicyLink>
        ) : (
            <span>{policyLinkText}</span>
        );
        return (
            <FormattedMessage
                id="payment.card.add.consent"
                defaultMessage={DEFAULT_MESSAGE['payment.card.add.consent']}
                values={{ serviceName, privacyPolicyLink: privacyPolicyLinkElement }}
            />
        );
    }

    public componentWillMount(): void {
        const { brandCodes } = this.props;
        const payVaultElementStyles = getPayVaultStylesByScreenSize();

        const payVaultElements: IPayVaultElements = {
            cardNumber: {
                mount: '#my-card-mount',
                styles: payVaultElementStyles,
                placeholder: '',
                brandCodes: brandCodes,
            },
            expirationMonth: {
                mount: '#my-expiration-month-mount',
                styles: payVaultElementStyles,
                format: 'MM',
                placeholder: 'MM',
            },
            expirationYear: {
                mount: '#my-expiration-year-mount',
                styles: payVaultElementStyles,
                format: 'YY',
                placeholder: 'YY',
            },
            cardScanModal: {
                lang: 'JA',
            },
        };
        if (!this.state.isEditing) {
            PayVaultAPIService.destroyIframe();
            PayVaultAPIService.setupPayVault(payVaultElements);
            PayVaultAPIService.isPayVaultLoaded().then((loaded) => {
                if (loaded) {
                    this.setState({ isPayVaultLoaded: true });
                }
            });
        }
    }

    public async handleSubmit(
        values: IPaymentMethodFormValues,
        actions: FormikActions<IPaymentMethodFormValues>,
    ) {
        const {
            onSubmit,
            card,
            addCard,
            addCardWithNewAddress,
            updateCard,
            showBillingAddress,
            challengerPid,
        } = this.props;
        const { isEditing } = this.state;
        const challengeData = getChallengeData(challengerPid);

        const newAddress = values.addressUuid
            ? undefined
            : ({
                  challengeData: challengeData,
                  firstName: values.firstName,
                  lastName: values.lastName,
                  companyName: values.companyName,
                  countryCode: values.countryCode,
                  addressLine1: values.addressLine1,
                  addressLine2: values.addressLine2,
                  city: values.city,
                  postalCode: values.postalCode,
                  phoneNumber: values.phoneNumber,
                  state: values.state,
                  stateCode: values.stateCode,
                  isShipping: false,
              } as IAddressRequest);

        if (isEditing && card) {
            const updatedCard: ICardPutRequest = {
                challengeData: challengeData,
                cardToken: card.cardToken,
                expireMonth: values.expireMonth,
                ownerName: values.ownerName,
                isPrimary: values.isPrimary === card.isPrimary ? undefined : values.isPrimary,
                expireYear: getFullYear(values.expireYear),
            };

            if (showBillingAddress) {
                updatedCard.addressUuid = values.addressUuid;
            }

            if (newAddress && showBillingAddress) {
                addCardWithNewAddress(updatedCard, newAddress, card.cardToken);
            } else {
                updateCard(card.cardToken, updatedCard);
            }

            onSubmit();
        } else {
            PayVaultAPIService.requestToken().then((json: IPayVaultAPISuccessResponse) => {
                const newCard: ICardPostRequest = {
                    challengeData: challengeData,
                    timestamp: json.timestamp,
                    maskedCardDetails: json.maskedCardDetails,
                    signature: json.signature,
                    keyVersion: json.keyVersion,
                    ownerName: values.ownerName,
                    isPrimary: values.isPrimary,
                };

                if (showBillingAddress) {
                    newCard.addressUuid = values.addressUuid || '';
                }

                if (newAddress && showBillingAddress) {
                    addCardWithNewAddress(newCard, newAddress);
                } else {
                    addCard(newCard);
                }

                onSubmit();
            });
        }
    }

    public render() {
        const { challengerPid } = this.props;
        const { initialValues, isSubmitting, isEditing, consentGiven } = this.state;

        return (
            <Form
                buttonTextLabel={isEditing ? 'button.save' : 'button.add'}
                id={PayVaultAPIFormKeys.form}
                value={initialValues}
                handleSubmit={this.handleSubmit}
                loading={isSubmitting}
                isTargetFieldChanged={isEditing || !this.showConsentCheckbox || consentGiven}
                renderFormFields={this.renderFormFields}
                renderExtraButtons={this.renderExtraButtons}
                challengerPid={challengerPid}
                onSubmitAttemptWithErrors={this.handleSubmitAttemptWithErrors}
                saveButtonProps={{
                    qaId: 'submit-payment-method',
                    width: '150px',
                }}
            />
        );
    }

    private renderFormFields(values: IPaymentMethodFormValues) {
        const {
            addresses,
            billingAddress,
            paymethod,
            showBillingAddress,
            addressFieldConfig,
            addressTemplate,
            locale,
            region,
            serviceName,
            intl,
        } = this.props;
        const { isEditing, isPayVaultLoaded, consentGiven } = this.state;

        const policyLinkText = intl.formatMessage(
            TranslationHelpers.getMessageDescriptor('payment.card.add.consent.policy.link'),
        );
        const consentLabelNode = this.renderConsentLabelNode(policyLinkText);

        return (
            <React.Fragment>
                <FormManditoryAstrixMessage />
                <CardFields
                    autoFocus={false}
                    isEditing={isEditing}
                    values={values}
                    cardFields={paymethod}
                    isPayVaultLoaded={isPayVaultLoaded}
                    region={region}
                />
                {showBillingAddress && (
                    <>
                        <Title>{TranslationHelpers.getFormattedMessage('address.billing')}</Title>
                        <AddressSelector
                            addresses={addresses}
                            selectedAddress={billingAddress}
                            addressFieldsValues={values}
                            addressFieldConfig={addressFieldConfig}
                            addressTemplate={addressTemplate}
                            locale={locale}
                        />
                    </>
                )}
                {this.showConsentCheckbox && (
                    <ConsentCheckboxWrapper>
                        <Checkbox
                            id="cardAddConsent"
                            name="cardAddConsent"
                            value={consentGiven}
                            onChange={this.handleConsentToggle}
                            ariaLabel={intl.formatMessage(
                                TranslationHelpers.getMessageDescriptor('payment.card.add.consent'),
                                { serviceName, privacyPolicyLink: policyLinkText },
                            )}
                            labelNode={consentLabelNode}
                            tooltipText={undefined}
                        />
                    </ConsentCheckboxWrapper>
                )}
            </React.Fragment>
        );
    }

    private renderExtraButtons(values: IPaymentMethodFormValues) {
        const { isOnlyCard } = this.props;
        const { isEditing, isPrimaryCard } = this.state;
        return (
            <SwitchField
                id="isPrimary"
                value={values.isPrimary}
                disabled={(isEditing && isPrimaryCard) || isOnlyCard}
                labelMessage={TranslationHelpers.getMessageDescriptor('payment.make_default')}
                tooltipText={
                    isEditing && isPrimaryCard
                        ? TranslationHelpers.getMessageDescriptor('tooltip.default_card.edit')
                        : TranslationHelpers.getMessageDescriptor(
                              isOnlyCard
                                  ? 'tooltip.default_card.create.only'
                                  : 'tooltip.default_card.create',
                          )
                }
            />
        );
    }
}
const injectedPaymentMethodForm = injectIntl(PaymentMethodForm);
export default injectedPaymentMethodForm;
